import React, { useEffect, useState, useRef } from 'react';
import Embed from './EmbedContent';
import { useLocation } from 'react-router-dom';
import GeneralTooltips from 'components/Dashboard/components/GeneralTooltips';
import normalize from 'components/Dashboard/utilities/normalizeString';
import ReactGA from 'react-ga4';
import createTypeOfResource from 'components/Dashboard/helpers/createTypeOfResource';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import validateParameter from 'utils/validateParameter';
import StakeHolderSharePopUp from 'components/Dashboard/StakeHolders/StakeHolderSharePopUp';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import Preloader from 'components/Dashboard/Analytics/ui/AnalyticsDashboard/Preloader';
import * as Sentry from '@sentry/react';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import useCreateFiltersForTypes from 'components/Dashboard/helpers/createFiltersForTypes';
import useCallEmbedParliamentLive from 'components/embed/useCallEmbedParliamentLive';
import useEmbedSpecificContentApiCall from 'components/embed/useEmbedSpecificContentApiCall';
import { useCallEventListItem } from 'components/Dashboard/LiveFromParliament/useEventListFunctions';
import useCreateInitialResources from 'components/Settings/WebsiteFeeds/useCreateInitialResources';

const EmbedPage = (props) => {
  const {
    keywordsLists,
    resources: propsResources,
    uponClickOption,
    isFromPopup,
    userId: propsUserId,
    customListName,
    designState,
    headerState,
    selectedTopics,
  } = props;
  const { headerValues, setHeaderValues } = headerState ?? {};
  const pageSize = parseInt(import.meta.env.VITE_APP_PAGE_SIZE);

  const source = new AbortController();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState({});
  const [switchingResults, setSwitchingResults] = useState(false);
  const showTweets = import.meta.env.VITE_APP_ENABLE_TWITTER === 'true';
  const [parliamentLiveReferenceDates, setParliamentLiveReferenceDates] = useState({});
  const [initialDate, setInitialDate] = useState(null);
  const { agencyUserBase } = useTopicOrClientWord();
  const { filterResources } = useCreateInitialResources();

  const normalizeSubheaderText = (text) => {
    if (!text) return text;
    // Replace "x posts" with "X posts" (case-insensitive)
    return text.replace(/\bx posts\b/gi, 'X posts');
  };

  const embedState = {
    topicsIds: selectedTopics
      ? selectedTopics?.map((item) => item?.id)
      : getUrlParam('topicsIds')
        ? validateParameter('topicsIds')
            ?.split(',')
            ?.map((item) => parseInt(item))
        : getUrlParam('keywordListId')
          ? [validateParameter('keywordListId')]
          : null,
    resources: filterResources((propsResources ?? validateParameter('resources'))?.split(',')).join(','),
    openItem: uponClickOption ?? validateParameter('openItem'),
    userId: propsUserId ?? parseInt(validateParameter('userId')),
    headerColour:
      designState?.headerColour ??
      (!!validateParameter('headerColour') ? validateParameter('headerColour') : '#ffffff'),
    background:
      designState?.backgroundColour ??
      (!!validateParameter('backgroundColour') ? validateParameter('backgroundColour') : '#ffffff'),
    includeBranding:
      designState?.includeBranding ??
      (validateParameter('includeBranding') !== '' ? validateParameter('includeBranding') : true),
    barColour:
      designState?.barColour ?? (!!validateParameter('barColour') ? validateParameter('barColour') : '#f9f9f9'),
    headerText:
      headerValues?.header ??
      (!!validateParameter('headerText') ? validateParameter('headerText') : `Latest political updates`),
    subheaderText: normalizeSubheaderText(
      headerValues?.subheader ??
        (!!validateParameter('subheaderText')
          ? validateParameter('subheaderText')
          : `Showing the latest political developments ${agencyUserBase ? 'relevant to' : 'related to'} ${
              results?.teamName ?? ''
            }`)
    ),
    teamId: props?.teamId ?? validateParameter('teamId'),
    hideResourcesWithoutContent: props?.hideResourcesWithoutContent ?? validateParameter('hideResourcesWithoutContent'),
  };

  const {
    topicsIds,
    resources,
    openItem,
    userId,
    headerColour,
    background,
    barColour,
    includeBranding,
    headerText,
    subheaderText,
    teamId,
    hideResourcesWithoutContent,
  } = embedState;

  const [mainIsLoading, setMainIsLoading] = useState(!!teamId && !!topicsIds);
  const [content, setContent] = useState(null);
  const [widgetKeywordsLists, setWidgetKeywordsLists] = useState(
    getUrlParam('keywordListId')
      ? [{ id: parseInt(getUrlParam('keywordListId')) }]
      : keywordsLists
        ? keywordsLists.filter((list) => topicsIds.includes(list.id))
        : []
  );
  const [topic, setTopic] = useState(
    getUrlParam('keywordListId')
      ? { id: parseInt(getUrlParam('keywordListId')) }
      : keywordsLists
        ? keywordsLists.find((item) => parseInt(item.id) === parseInt(topicsIds[0]))
        : {}
  );
  const [localResources, setLocalResources] = useState(resources);

  const includeBrandingProperty = typeof includeBranding === 'string' ? includeBranding === 'true' : includeBranding;
  const parliamentarianId = parseInt(validateParameter('parliamentarianId'));
  const appgId = parseInt(validateParameter('appgId'));
  const resourcesWithContent = useRef([]);

  const { generalApiCall } = useGeneralApiCall();
  const { createFiltersForTypes } = useCreateFiltersForTypes();
  const { specificContentApiCall } = useEmbedSpecificContentApiCall({
    includeBrandingProperty,
    initialDateState: { initialDate, setInitialDate },
    contentState: { content, setContent },
    results,
    topic,
  });

  const createContentType = () => {
    if (location.search !== '' || !!localResources) {
      let resourcesToCall = localResources?.split(',');
      if (!showTweets && !!resourcesToCall) {
        let tweetsPosition = resourcesToCall.indexOf('Tweets');
        if (tweetsPosition >= 0) {
          resourcesToCall.splice(tweetsPosition, 1);
        }
      }
      return resourcesToCall[0];
    }
    return '';
  };

  const [typeOfContent, setTypeOfContent] = useState(createContentType());
  const scrollbarContentContainer = useRef(null);
  const [heightContainer, containerRef] = useHeightContainer();

  useEffect(() => {
    document.getElementsByTagName('body')[0].classList.add('embed-widget-body');
    ReactGA.initialize(`${import.meta.env.VITE_APP_WIDGET_ANALYTICSCODE}`);
    ReactGA.send({ hitType: 'pageview', page: window.location.pathname + window.location.search });
  }, []);

  useEffect(() => {
    if (keywordsLists) {
      setWidgetKeywordsLists(keywordsLists.filter((list) => topicsIds.includes(list.id)));
      setTopic(keywordsLists.find((item) => parseInt(item.id) === parseInt(topicsIds[0])));
    }
  }, [selectedTopics]);

  useEffect(() => {
    if (resources !== localResources && !mainIsLoading) {
      let finalResources = !!resources ? resources?.split(',') : [];
      if (!showTweets) {
        let tweetsPosition = finalResources.indexOf('Tweets');
        if (tweetsPosition >= 0) {
          finalResources.splice(tweetsPosition, 1);
        }
      }
      finalResources = finalResources?.filter((item) => resourcesWithContent?.current?.includes(item));
      if (!!resources && finalResources[0] !== typeOfContent) {
        setTypeOfContent(finalResources[0]);
      }
      setLocalResources(finalResources?.join(','));
    }
  }, [resources, mainIsLoading]);

  useEffect(() => {
    const source = new AbortController();
    loadLists.current(source);
    return () => {
      source.abort('Embed initial load cancelled');
    };
  }, [hideResourcesWithoutContent, selectedTopics]);

  useCallEmbedParliamentLive({
    parliamentLiveReferenceDates,
    condition:
      typeOfContent === 'RealtimeUtterances' &&
      !isLoading &&
      location.pathname.startsWith('/embed') &&
      content === null,
    eventApiCall: async ({ source, filters }) => {
      const newResults = await apiCallPerContent({ type: typeOfContent, sourceProp: source, intervalFilters: filters });
      if (!!newResults && newResults?.hits?.length) {
        const finalResults = {
          ...newResults,
          hits: newResults.hits.map((item, index) => {
            return {
              ...item,
              freshItem: true,
            };
          }),
        };
        if (!!finalResults?.highlights) {
          finalResults?.hits.forEach((item, index) => {
            item.keywordListId =
              userId && finalResults?.feedItemFlags?.[index]
                ? finalResults?.feedItemFlags?.[index]?.matchingKeywordLists?.[0]?.id
                : null;
          });
        }
        setResults({ ...results, hits: [...finalResults?.hits, ...results?.hits] });
      }
      try {
      } catch (error) {}
    },
  });

  useCallEventListItem({
    condition: !!content?.item && content?.item?.type === 'RealtimeUtterance' && !content?.isLoading,
    eventId: content?.item?.eventId,
    data: content?.currentNew?.eventBodies,
    setData: (eventBodies) => {
      setContent({ ...content, currentNew: { ...content?.currentNew, eventBodies } });
    },
    date: initialDate,
  });

  const loadLists = useRef(null);
  loadLists.current = async (source) => {
    try {
      setMainIsLoading(true);
      if (teamId && topicsIds) {
        if (!keywordsLists) {
          const pathname = `/api/widget/team-topics-for-analytics?teamId=${teamId}&topicIds=${topicsIds.join(',')}`;
          const method = 'get';
          const results = await generalApiCall({ pathname, method, notShowErrorMessage: true, requestSource: source });
          if (!!results) {
            let lists = results.map((item) => {
              return { ...item, name: item.label, id: item.value };
            });
            setWidgetKeywordsLists(lists);
            setTopic(lists.find((item) => parseInt(item.id) === parseInt(topicsIds[0])));
          }
        }
      }
      const finalResources = await removeResourcesWithoutContent({ source });
      if (!!finalResources) {
        setMainIsLoading(false);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const removeResourcesWithoutContent = async ({ source }) => {
    try {
      if (hideResourcesWithoutContent) {
        const finalResources = !!resources ? resources?.split(',') : [];
        let finalItems = null;
        for (let i = 0; i < finalResources.length; i++) {
          const initialType = finalResources[i];
          const type = createTypeOfResource(initialType);
          const content = await apiCallPerContent({ type, sourceProp: source });
          if (!!content) {
            if (!finalItems) {
              finalItems = {};
            }
            if (content?.hits?.length > 0) {
              finalItems[initialType] = content?.hits?.length;
            }
          }
        }
        if (!!finalItems) {
          const keysFinalItems = Object.keys(finalItems);
          setLocalResources(keysFinalItems?.length > 0 ? keysFinalItems?.join(',') : resources);
          resourcesWithContent.current = keysFinalItems;
          setTypeOfContent(keysFinalItems?.length > 0 ? keysFinalItems[0] : finalResources[0]);
        }
        return finalItems;
      } else {
        setLocalResources(resources);
        return true;
      }
    } catch (error) {}
  };

  const apiCallPerContent = async ({ type, pageNumber, sourceProp, intervalFilters }) => {
    const url = type === 'RealtimeUtterances' ? `/api/widget/query-by-keyword-list-v2` : `/api/widget/query`;
    let requestBody = {
      type: type === 'RealtimeUtterances' ? 'RealtimeUtterance' : type,
      pageNumber: pageNumber ?? 1,
      pageSize: type === 'Event' ? 10000 : pageSize,
    };

    if (topic && (!userId || type === 'RealtimeUtterances')) {
      requestBody = { ...requestBody, keywordListId: topic?.id };
    }

    const { filters, sort, parliamentLiveReferenceDates } = createFiltersForTypes(type);
    requestBody.sort = sort;

    if (type === 'ParliamentaryRecord') {
      requestBody.collapseField = 'contentUrl';
    }

    if (filters) {
      requestBody.filters = filters;
    }

    if (type === 'RealtimeUtterances' && !!teamId) {
      requestBody = {
        ...requestBody,
        includeExplanations: true,
        includeHighlights: true,
      };
      if (!!teamId) {
        requestBody = { ...requestBody, teamId: teamId };
      }
    }

    if (userId) {
      if (type === 'RealtimeUtterances') {
        requestBody = {
          ...requestBody,
          keywordListId: null,
        };
      } else {
        if (!(type.toLowerCase() === 'event' || type.toLowerCase() === 'consultation')) {
          requestBody = {
            ...requestBody,
            userId,
            sort: {
              field: 'feedItemId',
              order: 'DESC',
            },
          };
        } else {
          requestBody = { ...requestBody, userId };
        }
      }
      if (teamId && agencyUserBase) {
        requestBody = { ...requestBody, teamId: teamId };
      }
    }

    if (parliamentarianId) {
      requestBody = { ...requestBody, parliamentarianId };
    }

    if (appgId) {
      requestBody = { ...requestBody, appgId };
    }

    if (intervalFilters) {
      requestBody = { ...requestBody, filters: intervalFilters };
    } else {
      if (type === 'RealtimeUtterances' && parliamentLiveReferenceDates) {
        setParliamentLiveReferenceDates(parliamentLiveReferenceDates);
      }
    }

    try {
      let results = await generalApiCall({
        method: 'post',
        requestProperties: requestBody,
        pathname: url,
        requestSource: sourceProp ?? source,
        notShowErrorMessage: true,
        bigInt: true,
      });
      let finalResults = results;
      if (!!finalResults?.highlights) {
        finalResults?.hits.forEach((item, index) => {
          item.highlights = finalResults?.highlights[index];
          if (type === 'RealtimeUtterances') {
            item.keywordListId =
              userId && finalResults?.feedItemFlags?.[index]
                ? finalResults?.feedItemFlags?.[index]?.matchingKeywordLists?.[0]?.id
                : null;
          } else {
            item.keywordListId =
              userId && finalResults?.matchedKeywordListIds[index] ? finalResults?.matchedKeywordListIds[index] : null;
          }
        });
      }
      return finalResults;
    } catch (error) {}
  };

  const load_more_results = async () => {
    if (results) {
      let { totalHits, pageNumber } = results;
      let number = pageNumber * pageSize;
      if (number < totalHits) {
        let newResults = await apiCallPerContent({
          type: createTypeOfResource(typeOfContent),
          pageNumber: pageNumber + 1,
        });
        const resultsJoined = results?.hits.concat(newResults.hits);
        const matchedKeywordListIdsJoined = results?.matchedKeywordListIds?.concat(
          newResults.matchedKeywordListIdsJoined
        );

        let finalResults = resultsJoined?.map((item, index) => {
          return {
            ...item,
            keywordListId: !!userId && matchedKeywordListIdsJoined[index] ? matchedKeywordListIdsJoined[index] : null,
          };
        });
        if (newResults) {
          setResults({
            ...results,
            hits: finalResults,
            explanations: results.explanations.concat(newResults.explanations),
            pageNumber: newResults.pageNumber,
          });
        }
      }
    }
  };

  const scrollbarContainer = useRef(null);

  const clickResources = async (item) => {
    if (scrollbarContainer.current) {
      scrollbarContainer.current.scrollToTop();
      scrollbarContainer.current.scrollToLeft();
    }
    source.abort('Resources cancelled');
    setSwitchingResults(true);
    setTypeOfContent(item);
  };

  const createFunctionOnClick = (item) => {
    return async (e) => {
      e.preventDefault();
      if (openItem === 'withinEmbed' && results.openContentInWidgetAllowed) {
        setContent({ isLoading: true, item });
      } else {
        window.open(
          `${item.contentUrl}?utm_source=${normalize(results.teamName)}-${normalize(
            results.topicName
          )}-embed-widget&utm_medium=referrer${
            item.contentType === 'ParliamentaryContribution' ? `#contribution-${item.contributionId}` : ''
          }`
        );
      }
    };
  };

  const createResources = () => {
    const tweetsEnabled = !!results ? results.tweetsEnabled : false;
    let finalResources = localResources;
    if ((localResources.includes('Tweets') && !tweetsEnabled) || !showTweets) {
      finalResources = localResources.replace('Tweets', '');
    }
    return !!results && Object.keys(results).length > 0 ? finalResources : '';
  };

  const clickOnArrow = (position) => {
    const resources = createResources();
    const items = resources.split(',');
    clickResources(items[position]);
  };

  const createTopicName = () => {
    if (customListName) {
      return customListName;
    } else {
      return !!results ? (userId ? results.teamName : results.topicName) : '';
    }
  };

  return (
    <>
      <div ref={containerRef} style={{ height: isFromPopup ? '100%' : `${heightContainer - 2}px` }}>
        {mainIsLoading ? (
          <div className='flex-centered justify-content-center bg-white w-100 h-100'>
            <Preloader />
          </div>
        ) : (
          <Embed
            scrollbarContainer={scrollbarContainer}
            resources={createResources()}
            clickResources={clickResources}
            isLoading={isLoading}
            results={results}
            typeOfContent={typeOfContent}
            topicName={createTopicName()}
            load_more_results={load_more_results}
            createFunctionOnClick={createFunctionOnClick}
            content={content}
            setContent={setContent}
            clickOnArrow={clickOnArrow}
            setIsLoading={setIsLoading}
            setResults={setResults}
            apiCallPerContent={apiCallPerContent}
            source={source}
            isFromPopup={isFromPopup}
            scrollbarContentContainer={scrollbarContentContainer}
            design={{
              headerColour,
              background,
              barColour,
              includeBranding: results?.removeBrandingAllowed ? includeBranding : true,
            }}
            header={{
              headerText,
              subheaderText,
              headerValues,
              setHeaderValues,
            }}
            widgetKeywordsLists={widgetKeywordsLists}
            topicState={{ topic, setTopic }}
            selectedTopics={selectedTopics ?? widgetKeywordsLists}
            userId={propsUserId}
            switchingResultsState={{ switchingResults, setSwitchingResults }}
            includeBrandingProperty={includeBrandingProperty}
            specificContentApiCall={specificContentApiCall}
            hideResourcesWithoutContent={hideResourcesWithoutContent}
          />
        )}
      </div>
      {location.pathname.startsWith('/embed') && <StakeHolderSharePopUp />}
      <GeneralTooltips />
    </>
  );
};

export default EmbedPage;
